/**
 * Back Roads: a trip through New York State in a chosen year.
 *
 * WHO THIS IS FOR. People who knew the state before the interstates, who are
 * slower on a phone than they are in their heads. So: nothing is timed,
 * nothing is lost, every control is big, every pin is confirmed before it
 * counts, the text can be read aloud, and the map is a road map with real
 * relief rather than a satellite photograph, because nobody in 1952 saw
 * Warren County from orbit. They saw the Esso map.
 *
 * NOTHING SCROLLS WHILE PLAYING. A page that scrolls under a thumb that
 * meant to tap is the single most confusing thing a phone does to an older
 * player, so the play screen is the height of the viewport: a header, the
 * map, and one card. Only the card's own contents can scroll, and only when
 * a summary is longer than the screen. Picking a trip is two short screens
 * for the same reason: choose an era, then choose a trip in it.
 *
 * ENTIRELY OFFLINE. No gateway, no socket, no account. The trip lives in the
 * address so a link to one can be handed to somebody, and the best score
 * lives in this browser. The map geometry is fetched from this origin the
 * first time a trip opens and never again.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ANCHORS,
  ERAS,
  TRIPS,
  allPlaces,
  eraById,
  isAnchor,
  placeById,
  regionById,
  routeLine,
  routesFor,
  tripById,
} from '@geogame/data/backroads';
import type { Era, EraId, Place, Trip } from '@geogame/data/backroads';
import type { LngLat } from '@geogame/shared';

import { BackRoadsMap } from '../components/BackRoadsMap';
import type {
  Anchor,
  BackRoadsDetail,
  BackRoadsMapData,
  BackRoadsMapHandle,
  RouteLine,
  TownDot,
} from '../components/BackRoadsMap';
import { BackRoadsQuestion } from '../components/BackRoadsQuestions';
import {
  answerQuestion,
  askQuestion,
  confirmPin,
  currentStop,
  formatMiles,
  maxPoints,
  movePin,
  nextStop,
  placePin,
  shareLine,
  startTrip,
  stars,
  totalPoints,
} from '../lib/backroads';
import type { TripState } from '../lib/backroads';
import { decodeDetail, isDetailFile } from '../lib/backroadsDetail';
import { navigate } from '../lib/router';
import { readLocal, writeLocal } from '../store/storage';

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function bestKey(tripId: string): string {
  return `backroads.best.${tripId}`;
}

function readBest(tripId: string): number | null {
  const raw = readLocal(bestKey(tripId));
  if (raw === null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** Read text aloud, when the browser can. Silent otherwise. */
function speak(text: string): void {
  try {
    const synth = window.speechSynthesis;
    if (!synth) return;
    synth.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.92;
    synth.speak(utterance);
  } catch {
    // A browser without speech is a browser without the button; see canSpeak.
  }
}

function canSpeak(): boolean {
  try {
    return typeof window !== 'undefined' && 'speechSynthesis' in window && typeof SpeechSynthesisUtterance === 'function';
  } catch {
    return false;
  }
}

function Stars({ count }: { readonly count: 1 | 2 | 3 }): React.JSX.Element {
  return (
    <span className="gg-br-stars" aria-label={`${count} of 3 stars`}>
      {'★'.repeat(count)}
      <span className="gg-br-stars-off">{'★'.repeat(3 - count)}</span>
    </span>
  );
}

function BetaBadge({ large = false }: { readonly large?: boolean }): React.JSX.Element {
  return <span className={large ? 'gg-beta gg-beta-large' : 'gg-beta'}>Beta</span>;
}

/** A box around two points with a margin, so the reveal shows both pins with room. */
function boxAround(a: LngLat, b: LngLat): readonly [number, number, number, number] {
  const minLng = Math.min(a[0], b[0]);
  const maxLng = Math.max(a[0], b[0]);
  const minLat = Math.min(a[1], b[1]);
  const maxLat = Math.max(a[1], b[1]);
  const pad = Math.max(0.25, (maxLng - minLng) * 0.4, (maxLat - minLat) * 0.4);
  return [minLng - pad, minLat - pad, maxLng + pad, maxLat + pad];
}

/** Every place the trip is drawn for, so the whole trip can be framed at the end. */
function boxAroundAll(points: readonly LngLat[]): readonly [number, number, number, number] {
  let minLng = Number.POSITIVE_INFINITY;
  let minLat = Number.POSITIVE_INFINITY;
  let maxLng = Number.NEGATIVE_INFINITY;
  let maxLat = Number.NEGATIVE_INFINITY;
  for (const [lng, lat] of points) {
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  }
  const pad = 0.2;
  return [minLng - pad, minLat - pad, maxLng + pad, maxLat + pad];
}

function eraSpan(era: Era): string {
  return `${era.years[0]} to ${era.years[1] >= 2100 ? 'today' : era.years[1]}`;
}

// ---------------------------------------------------------------------------
// Choosing a trip: an era, then a trip in it
// ---------------------------------------------------------------------------

function TripCard({ trip }: { readonly trip: Trip }): React.JSX.Element {
  const best = readBest(trip.id);
  const max = maxPoints(trip);
  return (
    <button
      type="button"
      className="gg-br-trip"
      onClick={() => navigate({ name: 'backroads', tripId: trip.id })}
    >
      <span className="gg-br-trip-title">{trip.title}</span>
      <span className="gg-br-trip-meta">
        {trip.year} · {trip.stops.length} stops
        {best === null ? null : (
          <>
            {' · '}
            <Stars count={stars(best, max)} /> {best} of {max}
          </>
        )}
      </span>
    </button>
  );
}

function TripPicker(): React.JSX.Element {
  const [eraId, setEraId] = useState<EraId | null>(null);
  const era = eraId === null ? null : eraById(eraId);
  const trips = era === null ? [] : TRIPS.filter((t) => t.era === era.id);

  return (
    <div className="gg-br-page gg-br-fixed">
      <main className="gg-br gg-br-pick">
        <header className="gg-br-hero">
          <h1 className="gg-br-title">
            Back Roads <BetaBadge large />
          </h1>
          {era === null ? (
            <p className="gg-br-lede">
              A drive through the New York you remember, one stop at a time. No clock, nothing to
              lose. First, pick a time.
            </p>
          ) : (
            <p className="gg-br-lede">
              {eraSpan(era)}. {era.travel}. New York to Buffalo: {era.nycToBuffalo.toLowerCase()}.
            </p>
          )}
        </header>

        {era === null ? (
          <div className="gg-br-eras">
            {ERAS.map((e) => {
              const count = TRIPS.filter((t) => t.era === e.id).length;
              if (count === 0) return null;
              return (
                <button key={e.id} type="button" className="gg-br-era-btn" onClick={() => setEraId(e.id)}>
                  <span className="gg-br-era-btn-name">{e.name}</span>
                  <span className="gg-br-era-btn-years">
                    {eraSpan(e)} · {count} {count === 1 ? 'trip' : 'trips'}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="gg-br-trips">
            {trips.map((t) => (
              <TripCard key={t.id} trip={t} />
            ))}
          </div>
        )}

        <section className="gg-br-actions">
          {era === null ? (
            <button type="button" className="gg-br-btn gg-br-btn-quiet" onClick={() => navigate({ name: 'home' })}>
              Back to Worldly
            </button>
          ) : (
            <button type="button" className="gg-br-btn gg-br-btn-quiet" onClick={() => setEraId(null)}>
              Other times
            </button>
          )}
        </section>
        <p className="gg-br-credit">
          Beta: some facts may be off. Map data from OpenStreetMap contributors (ODbL), Natural Earth,
          the US Census Bureau and USGS.
        </p>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One trip
// ---------------------------------------------------------------------------

/** Fetched once per page; the second trip opens instantly. */
let mapDataCache: BackRoadsMapData | null = null;

function useMapData(): BackRoadsMapData | null {
  const [data, setData] = useState<BackRoadsMapData | null>(mapDataCache);
  useEffect(() => {
    if (data) return undefined;
    let live = true;
    void import('@geogame/data/backroads-map.json')
      .then((mod) => {
        const loaded = (mod as { default: unknown }).default as BackRoadsMapData;
        mapDataCache = loaded;
        if (live) setData(loaded);
      })
      .catch((err: unknown) => {
        console.warn('backroads: map geometry failed to load', err);
      });
    return () => {
      live = false;
    };
  }, [data]);
  return data;
}

/** The big file, fetched once from this origin. Null until it lands; the map works without it. */
let detailCache: BackRoadsDetail | null = null;

function useDetail(): BackRoadsDetail | null {
  const [detail, setDetail] = useState<BackRoadsDetail | null>(detailCache);
  useEffect(() => {
    if (detail) return undefined;
    let live = true;
    void fetch('/backroads/detail.json')
      .then((res) => {
        if (!res.ok) throw new Error(`detail.json: ${res.status}`);
        return res.json() as Promise<unknown>;
      })
      .then((raw) => {
        if (!isDetailFile(raw)) throw new Error('detail.json: not the shape the bake writes');
        const loaded = decodeDetail(raw);
        detailCache = loaded;
        if (live) setDetail(loaded);
      })
      .catch((err: unknown) => {
        console.warn('backroads: the detail layer failed to load', err);
      });
    return () => {
      live = false;
    };
  }, [detail]);
  return detail;
}

/** Dot size by kind. Zero means not drawn. */
const TOWN_SIZE: Record<Place['kind'], 0 | 1 | 2 | 3> = {
  city: 3,
  village: 2,
  hamlet: 1,
  lost: 1,
  peak: 0,
  landmark: 0,
};

function townDots(): readonly TownDot[] {
  const out: TownDot[] = [];
  for (const p of allPlaces()) {
    const size = TOWN_SIZE[p.kind];
    if (size === 0 || isAnchor(p.id)) continue;
    const dot: TownDot = { id: p.id, lngLat: p.lngLat, size };
    out.push({
      ...dot,
      ...(p.from === undefined ? {} : { from: p.from }),
      ...(p.to === undefined ? {} : { to: p.to }),
    });
  }
  return out;
}

function ReadAloud({ text, enabled }: { readonly text: string; readonly enabled: boolean }): React.JSX.Element | null {
  if (!enabled) return null;
  return (
    <button type="button" className="gg-br-btn gg-br-btn-quiet gg-br-read" onClick={() => speak(text)}>
      Read it to me
    </button>
  );
}

function TripPlay({ trip }: { readonly trip: Trip }): React.JSX.Element {
  const [state, setState] = useState<TripState>(() => startTrip(trip));
  const [introSeen, setIntroSeen] = useState(false);
  const mapData = useMapData();
  const detail = useDetail();
  const mapRef = useRef<BackRoadsMapHandle | null>(null);
  const speech = useMemo(canSpeak, []);

  const era = eraById(trip.era);
  const region = regionById(trip.region);
  const stop = currentStop(state);
  const place: Place = placeById(stop.place);
  const target = place.lngLat;
  const seed = `${trip.id}:${state.stopIndex}`;

  const routes = useMemo<readonly RouteLine[]>(
    () =>
      routesFor(trip.year).map((r) =>
        r.to === undefined
          ? { id: r.id, mode: r.mode, from: r.from, line: routeLine(r) }
          : { id: r.id, mode: r.mode, from: r.from, to: r.to, line: routeLine(r) },
      ),
    [trip.year],
  );
  const anchors = useMemo<readonly Anchor[]>(
    () => ANCHORS.map((id) => placeById(id)).map((p) => ({ id: p.id, name: p.name, lngLat: p.lngLat })),
    [],
  );
  const towns = useMemo(townDots, []);
  const visited = useMemo<readonly LngLat[]>(
    () => state.log.map((r) => placeById((trip.stops[r.stopIndex] as { place: string }).place).lngLat),
    [state.log, trip.stops],
  );

  const revealed = state.phase === 'pinned' || state.phase === 'question' || state.phase === 'answered';
  const interactive = introSeen && (state.phase === 'pin' || state.phase === 'confirm');

  // Frame the trip's region at the start of every stop, and both pins at the reveal.
  const lastFramedStop = useRef(-1);
  useEffect(() => {
    if (state.phase === 'pin' && lastFramedStop.current !== state.stopIndex) {
      lastFramedStop.current = state.stopIndex;
      mapRef.current?.fitBounds(region.bbox, state.stopIndex === 0 ? 0 : 800);
    }
  }, [state.phase, state.stopIndex, region.bbox]);
  useEffect(() => {
    if (state.phase === 'pinned' && state.pin) mapRef.current?.fitBounds(boxAround(state.pin, target), 900);
  }, [state.phase, state.pin, target]);
  useEffect(() => {
    if (state.phase === 'done') {
      mapRef.current?.fitBounds(boxAroundAll(trip.stops.map((s) => placeById(s.place).lngLat)), 900);
    }
  }, [state.phase, trip.stops]);

  // Remember the best run.
  useEffect(() => {
    if (state.phase !== 'done') return;
    const points = totalPoints(state);
    const best = readBest(trip.id);
    if (best === null || points > best) writeLocal(bestKey(trip.id), String(points));
  }, [state, trip.id]);

  const onTap = useCallback((lngLat: LngLat) => setState((s) => placePin(s, lngLat)), []);

  const [copied, setCopied] = useState(false);
  const copyShare = (): void => {
    const line = shareLine(state);
    try {
      void navigator.clipboard?.writeText(line).then(() => setCopied(true));
    } catch {
      // Nothing to do: the line is on screen to read out instead.
    }
  };

  const points = totalPoints(state);
  const max = maxPoints(trip);
  const stopLabel = `Stop ${state.stopIndex + 1} of ${trip.stops.length}`;
  const promptText = stop.clue ? `Find this place. ${stop.clue}` : `Find ${place.name}.`;

  return (
    <div className="gg-br-page gg-br-fixed">
      <main className="gg-br gg-br-play">
        <header className="gg-br-top">
          <button
            type="button"
            className="gg-br-btn gg-br-btn-quiet"
            onClick={() => navigate({ name: 'backroads', tripId: null })}
          >
            Trips
          </button>
          <div className="gg-br-top-title">
            <h1 className="gg-br-heading">{trip.title}</h1>
            <p className="gg-br-top-meta">
              {era.name} · {trip.year}
              {state.phase === 'done' ? '' : ` · ${stopLabel}`}
            </p>
          </div>
          <div className="gg-br-points tabular-nums" aria-label="Points so far">
            {points}
          </div>
        </header>

        <div className="gg-br-mapwrap">
          <BackRoadsMap
            ref={mapRef}
            data={mapData}
            detail={detail}
            year={trip.year}
            routes={routes}
            anchors={anchors}
            towns={towns}
            visited={visited}
            youPin={state.pin}
            targetPin={revealed ? target : null}
            interactive={interactive}
            onTap={onTap}
          />
          {mapData === null ? <div className="gg-br-loading">Unfolding the map…</div> : null}
          <div className="gg-br-mapcontrols" role="group" aria-label="Map controls">
            <button type="button" className="gg-br-mapbtn" aria-label="Zoom in" onClick={() => mapRef.current?.zoomBy(1)}>
              +
            </button>
            <button type="button" className="gg-br-mapbtn" aria-label="Zoom out" onClick={() => mapRef.current?.zoomBy(-1)}>
              −
            </button>
            <button type="button" className="gg-br-mapbtn gg-br-mapbtn-wide" onClick={() => mapRef.current?.fitBounds(region.bbox)}>
              {region.name}
            </button>
            {region.id === 'state' ? null : (
              <button
                type="button"
                className="gg-br-mapbtn gg-br-mapbtn-wide"
                onClick={() => mapRef.current?.fitBounds(regionById('state').bbox)}
              >
                Whole state
              </button>
            )}
          </div>
          <div className="gg-br-mapcredit">© OpenStreetMap contributors · Natural Earth · USGS</div>
        </div>

        <section className="gg-br-card" aria-live="polite">
          {!introSeen ? (
            <>
              <p className="gg-br-kicker">
                {era.name} · {trip.year}
              </p>
              <p className="gg-br-intro">{trip.intro}</p>
              <div className="gg-br-row">
                <button type="button" className="gg-br-btn gg-br-btn-primary" onClick={() => setIntroSeen(true)}>
                  Let us go
                </button>
                <ReadAloud text={trip.intro} enabled={speech} />
              </div>
            </>
          ) : null}

          {introSeen && (state.phase === 'pin' || state.phase === 'confirm') ? (
            <>
              <div className="gg-br-kickrow">
                <p className="gg-br-kicker">{stopLabel}</p>
                <ReadAloud text={promptText} enabled={speech} />
              </div>
              {stop.clue ? (
                <>
                  <h2 className="gg-br-question">Find this place</h2>
                  <p className="gg-br-clue">{stop.clue}</p>
                </>
              ) : (
                <h2 className="gg-br-question">Find {place.name}</h2>
              )}
              {state.phase === 'pin' ? (
                <p className="gg-br-hint">
                  {state.pin ? 'Tap the map again to move your pin.' : 'Tap the map where you think it is. Zoom in first if you like.'}
                </p>
              ) : (
                <>
                  <p className="gg-br-hint">Is this your spot?</p>
                  <div className="gg-br-row">
                    <button type="button" className="gg-br-btn gg-br-btn-primary" onClick={() => setState(confirmPin)}>
                      Yes, that is it
                    </button>
                    <button type="button" className="gg-br-btn" onClick={() => setState(movePin)}>
                      Let me move it
                    </button>
                  </div>
                </>
              )}
            </>
          ) : null}

          {state.phase === 'pinned' && state.pinResult ? (
            <>
              <p className="gg-br-kicker">{place.name}</p>
              <h2 className="gg-br-question">
                {state.pinResult.verdict}
                <span className="gg-br-award tabular-nums"> +{state.pinResult.points}</span>
              </h2>
              <p className="gg-br-body">
                Your pin was {formatMiles(state.pinResult.miles)} from {place.name}.
                {place.fate ? ` ${place.fate}` : ''}
              </p>
              <button type="button" className="gg-br-btn gg-br-btn-primary" onClick={() => setState(askQuestion)}>
                On to the question
              </button>
            </>
          ) : null}

          {state.phase === 'question' ? (
            <>
              <div className="gg-br-kickrow">
                <p className="gg-br-kicker">{place.name} · Question</p>
                <ReadAloud text={stop.question.text} enabled={speech} />
              </div>
              <h2 className="gg-br-question">{stop.question.text}</h2>
              <BackRoadsQuestion
                question={stop.question}
                seed={seed}
                onAnswer={(answer) => setState((s) => answerQuestion(s, answer))}
              />
            </>
          ) : null}

          {state.phase === 'answered' && state.questionResult ? (
            <>
              <div className="gg-br-kickrow">
                <p className="gg-br-kicker">{place.name}</p>
                <ReadAloud
                  text={`${stop.question.explain} Postcard from ${place.name}. ${stop.postcard}`}
                  enabled={speech}
                />
              </div>
              <h2 className="gg-br-question" data-correct={state.questionResult.correct ? 'true' : 'false'}>
                {state.questionResult.verdict}
                <span className="gg-br-award tabular-nums"> +{state.questionResult.points}</span>
              </h2>
              <p className="gg-br-body">{stop.question.explain}</p>
              <div className="gg-br-postcard">
                <p className="gg-br-postcard-kicker">Postcard from {place.name}</p>
                <p className="gg-br-postcard-text">{stop.postcard}</p>
              </div>
              <button type="button" className="gg-br-btn gg-br-btn-primary" onClick={() => setState(nextStop)}>
                {state.stopIndex + 1 >= trip.stops.length ? 'See how you did' : 'Next stop'}
              </button>
            </>
          ) : null}

          {state.phase === 'done' ? (
            <>
              <p className="gg-br-kicker">Trip complete</p>
              <h2 className="gg-br-question">
                <Stars count={stars(points, max)} />
                <span className="tabular-nums">
                  {' '}
                  {points} of {max}
                </span>
              </h2>
              <div className="gg-br-row">
                <button type="button" className="gg-br-btn gg-br-btn-primary" onClick={() => navigate({ name: 'backroads', tripId: null })}>
                  Another trip
                </button>
                <button
                  type="button"
                  className="gg-br-btn"
                  onClick={() => {
                    setState(startTrip(trip));
                    setIntroSeen(true);
                  }}
                >
                  Drive it again
                </button>
                <button type="button" className="gg-br-btn gg-br-btn-quiet" onClick={copyShare}>
                  {copied ? 'Copied' : 'Copy a line to share'}
                </button>
              </div>
              <ol className="gg-br-summary">
                {state.log.map((r) => {
                  const s = trip.stops[r.stopIndex];
                  if (!s) return null;
                  const p = placeById(s.place);
                  return (
                    <li key={r.stopIndex} className="gg-br-summary-row">
                      <span className="gg-br-summary-name">{p.name}</span>
                      <span className="gg-br-summary-detail">
                        {formatMiles(r.pinResult.miles)} off · {r.questionResult.correct ? 'right' : 'not this time'}
                      </span>
                      <span className="gg-br-summary-points tabular-nums">
                        {r.pinResult.points + r.questionResult.points}
                      </span>
                    </li>
                  );
                })}
              </ol>
            </>
          ) : null}
        </section>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The route
// ---------------------------------------------------------------------------

export function BackRoadsScreen({ tripId }: { readonly tripId: string | null }): React.JSX.Element {
  if (tripId === null) return <TripPicker />;
  const trip = tripById(tripId);
  if (!trip) {
    return (
      <div className="gg-br-page gg-br-fixed">
        <main className="gg-br gg-br-pick">
          <header className="gg-br-hero">
            <h1 className="gg-br-title">No such trip</h1>
            <p className="gg-br-lede">That road is not on the map. Pick one from the list.</p>
          </header>
          <section className="gg-br-actions">
            <button type="button" className="gg-br-btn gg-br-btn-primary" onClick={() => navigate({ name: 'backroads', tripId: null })}>
              All trips
            </button>
          </section>
        </main>
      </div>
    );
  }
  // Keyed on the trip so a link to a different trip starts fresh.
  return <TripPlay key={trip.id} trip={trip} />;
}
